import React, { useState } from "react";
import { api, errMsg } from "../lib/api";
import { Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";

export default function ExecutiveSummaryButton({ projectId, locked, onLocked }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tone, setTone] = useState("neutral");

  const close = () => setOpen(false);

  const generate = async (toneOverride) => {
    const useTone = toneOverride || tone;
    if (locked) { onLocked && onLocked(); return; }
    setOpen(true); setLoading(true); setData(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/executive-summary`, { tone: useTone });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
      setOpen(false);
    }
    setLoading(false);
  };

  const copy = () => {
    if (!data?.summary) return;
    const disclaimer =
      "\n\n--- AI-Generated Content Notice ---\n" +
      "This executive summary was generated using artificial intelligence and may contain inaccuracies, omissions, or unintended interpretations. Users should review and validate all content before relying on it for business, legal, financial, or strategic decisions. The user is responsible for determining the suitability and accuracy of the final output for their intended use.\n\n" +
      "AI-generated output should be considered a drafting aid and not professional advice. Human review is required prior to distribution or decision-making.";
    navigator.clipboard.writeText(data.summary + disclaimer);
    toast.success("Summary + AI disclaimer copied");
  };

  return (
    <>
      <button onClick={() => generate()} data-testid="exec-summary-btn"
        className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg shadow-blue-900/30 disabled:opacity-50">
        <Sparkles size={14}/> Executive summary
      </button>
      <Modal open={open} onClose={close} testId="exec-summary-modal">
        <ModalHeader icon={Sparkles} title="Executive summary" onClose={close} testId="exec-summary-close" />
        <div className="flex items-center gap-2 mb-4" data-testid="tone-picker">
          <span className="text-[10px] uppercase tracking-widest text-slate-400">Tone</span>
          {[
            {k:"optimistic", l:"Optimistic"},
            {k:"neutral", l:"Neutral"},
            {k:"cautious", l:"Cautious"},
          ].map((t) => (
            <button key={t.k} data-testid={`tone-${t.k}`} onClick={() => { setTone(t.k); if (data) generate(t.k); }}
              className={`text-xs px-2.5 py-1 rounded-full border ${tone===t.k?"bg-blue-500/15 text-blue-300 border-blue-500/40":"text-slate-300 border-slate-700 hover:border-slate-500"}`}>
              {t.l}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-300"><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div> Writing a status paragraph…</div>
        ) : data ? (
          <>
            <p className="text-slate-100 leading-relaxed text-[15px]" data-testid="exec-summary-text">{data.summary}</p>
            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-slate-500">
              <div>Delivery: <span className="text-slate-200">{data.metrics?.calculated_delivery}</span></div>
              <div>Risk: <span className="text-slate-200">{data.metrics?.risk_score}/100 {data.metrics?.risk_tier}</span></div>
              <div>Schedule variance: <span className="text-slate-200">{data.metrics?.schedule_variance_days ?? "—"}d</span></div>
              <div>Changes last 7 days: <span className="text-slate-200">{data.metrics?.changes_last_7_days}</span></div>
            </div>
            <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-[11px] leading-snug text-amber-200/90" data-testid="exec-summary-disclaimer">
              <div className="font-semibold text-amber-200 mb-1 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                AI-Generated Content Notice
              </div>
              <p className="mb-1.5">
                This executive summary was generated using artificial intelligence and may contain inaccuracies, omissions, or unintended interpretations. Users should review and validate all content before relying on it for business, legal, financial, or strategic decisions. The user is responsible for determining the suitability and accuracy of the final output for their intended use.
              </p>
              <p>
                AI-generated output should be considered a drafting aid and not professional advice. Human review is required prior to distribution or decision-making.
              </p>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => generate()} className="border border-slate-700 hover:border-blue-500 text-slate-200 text-sm px-3 py-2 rounded-lg">Regenerate</button>
              <button onClick={copy} data-testid="exec-summary-copy" className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg text-sm inline-flex items-center gap-1"><Copy size={12}/> Copy</button>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
}
